import throttle from 'lodash/throttle';
import resolveElement from 'lib/resolveElement';
import consolv from 'lib/consolv';
import MCP from './MCP';

const defaultConfiguration = {
  photoGallery: {
    enabled: false,
    activeSlots: {
      // leaderboard: { selector: '#dfp_leaderboard', refreshRate: 2 },
      bigbox: { selector: '#overlay_bigbox', refreshRate: 1 },
      // interstitial: { selector: '#photo_interstitial_wrapper_1', refreshRate: 4 },
    },
    refreshThrottle: 4000,
    refreshRate: 2,
    interstitialFrequency: 3,
  },
};

/**
 * Handles desktop photo gallery-ad slot synchronization with configurable frequency rates
 */
export default class PhotoGallery {
  _currentIncrement = 0;

  _configuration;

  _mcp;

  isReady = false;

  ready;

  _throttledRefreshTest;

  _interstitialContainer;

  /**
   * @param {MCPInstance} MCPRef - reference to the MCP library
   * @param {Object} configuration - configuration for the photo gallery functionality
   */
  constructor(MCPRef, configuration = {}) {
    /* eslint-disable no-underscore-dangle */
    this._constructorAsync = this._constructorAsync.bind(this);
    this._interstitialTest = this._interstitialTest.bind(this);
    this._refreshTest = this._refreshTest.bind(this);
    /* eslint-enable no-underscore-dangle */
    this.setSyncSlot = this.setSyncSlot.bind(this);
    this.setInterstitialSlot = this.setInterstitialSlot.bind(this);
    this.removeSyncSlot = this.removeSyncSlot.bind(this);
    this.next = this.next.bind(this);
    this.previous = this.previous.bind(this);

    this._mcp = MCPRef;
    this.ready = this._constructorAsync(configuration);
  }

  _constructorAsync(configuration) {
    /* eslint-disable-next-line no-underscore-dangle */
    return this._mcp.ready.then(() => {
      this.isReady = true;

      const fromConfigurator =
        this._mcp.Configurator.getConfiguration({ defaults: defaultConfiguration }).photoGallery;

      this._configuration = {
        ...fromConfigurator,
        ...configuration,
      };

      // creates a throttled version of the _refreshTest function to stop refreshes from happening
      // too quickly
      this._throttledRefreshTest = throttle(this._refreshTest, this._configuration.refreshThrottle,
        { leading: true, trailing: false });
      return this;
    });
  }

  /**
   * Allows sync slots to be added after the initial load of the photo gallery module
   * @param {string} slotType - a name or identifier of the slot(s) being targeted
   * @param {string} selector - an element selector that matches desired elements
   * @param {number} refreshRate - frequency of ad refreshes based on photo navigation events
   */
  setSyncSlot(slotType, selector, refreshRate, additionalConfiguration) {
    return this.ready.then(() => {
      const rule = { ...additionalConfiguration, selector };
      if (refreshRate) rule.refreshRate = refreshRate;

      this._configuration.activeSlots[slotType] =
        { ...this._configuration.activeSlots[slotType], ...rule };
    });
  }

  setInterstitialSlot(elementReference) {
    return this.ready.then(() => {
      const element = resolveElement(elementReference);
      if (element) {
        this._configuration.activeSlots.interstitial = element;

        // this._configuration.interstitialSlot = element;
      }
    });
  }

  /**
   * Allows sync slots to be removed after the initial load of the photo gallery module
   * @param {string} slotType - a name or identifier of the slot(s) being targeted
   */
  removeSyncSlot(slotType) {
    return this.ready.then(() => {
      if (this._configuration.activeSlots[slotType]) {
        delete this._configuration.activeSlots[slotType];
      }
    });
  }

  /**
   * Increments the current incrementer for the photo gallery and tests refresh condition
   */
  next() {
    const response = { showInterstitial: false };

    if (this.isReady && this._configuration.enabled) {
      this._currentIncrement += 1;
      response.showInterstitial = this._interstitialTest();
      this._throttledRefreshTest();
    }
    return response;
  }

  /**
   * Increments the current incrementer for the photo gallery and tests refresh condition, for
   * reverse navigation
   */
  previous() {
    const response = { showInterstitial: false };

    if (this.isReady && this._configuration.enabled) {
      this._currentIncrement += 1;
      response.showInterstitial = this._interstitialTest();
      this._throttledRefreshTest();
    }

    return response;
  }

  /**
   * Unthrottled function that checks the refresh frequency of each configured slot and refreshes,
   * if necessary
   */
  _refreshTest() {
    const { activeSlots } = this._configuration;
    let shouldTick = false;
    Object.keys(activeSlots).forEach((slotType) => {
      // interstitialTest handles interstitial slots
      if (slotType === 'interstitial') return;

      const { selector, refreshRate } = activeSlots[slotType];
      const rate =
        (typeof refreshRate === 'number') ? refreshRate : this._configuration.refreshRate;

      if (rate < 1 || this._currentIncrement === 0 || this._currentIncrement % rate) return;

      document.querySelectorAll(selector).forEach((element) => {
        const slot = this._mcp.slots.get(element);

        if (slot) {
          // Queue refresh rather than initiate immediately
          slot.setMetaData('forceRefresh', true);
          shouldTick = true;
        }
      });
    });

    // Tell Kronos to tick now so we don't have to wait up to a second to initiate a refresh
    if (shouldTick && this._mcp.Kronos) this._mcp.Kronos.tick();
  }

  _interstitialTest() {
    const {
      interstitialFrequency,
      activeSlots,
      galleryContainer,
    } = this._configuration;

    const { _currentIncrement } = this;

    const { interstitial } = activeSlots;
    if (!interstitial) return false;

    const interstitialSlot = this._mcp.getSlot(interstitial.selector);
    if (!interstitialSlot) {
      consolv.log('DatMCP PhotoGallery: Interstitial slot invalid');
      return false;
    }

    if (
      // !galleryContainer ||
      !interstitialSlot ||
      typeof interstitialFrequency !== 'number' ||
      _currentIncrement < 1 ||
      interstitialFrequency < 1 ||
      this._currentIncrement % interstitialFrequency !== 0
    ) {
      return false;
    }

    const slotsToRefresh = [interstitialSlot];
    // interstitialSlot.setTargeting('inttype', 'pislot');

    if (activeSlots.bigbox) {
      const bigbox = this._mcp.getSlot(activeSlots.bigbox.selector);
      // bigbox.setMetaData('counter', 0);
      // bigbox.setTargeting('inttype', 'pislot');
      slotsToRefresh.push(bigbox);
    }

    this._mcp.refreshSlots(slotsToRefresh);
    // galleryContainer.classList.add('interstitial-show');
    return true;
  }
}
